/****************************************************
 * Module Name:  TenderLotPlanning.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useState } from "react";
import Constants from "../../../utility/constants";
import { Badge, Box, Button, CircularProgress, Stack, Tooltip, Typography } from "@mui/material";
import Utility from "../../../utility/utility";
import NoPermission from "../../common/noPermission";
import CommonSnackbar from "../../common/commonSnackBar";
import TenderLotProxy from "../../../utility/proxy/manager/tender/tenderLotProxy";
import { BiSearchAlt2 } from "react-icons/bi";
import Select from "react-select";
import { LoadingButton } from "@mui/lab";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import renderCellExpand from "../../common/dataGridCellExpand";
import { MdOutlineAddPhotoAlternate } from "react-icons/md";
import VideoLibraryTwoToneIcon from '@mui/icons-material/VideoLibraryTwoTone';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { BackupTable } from "@mui/icons-material";
import Papa from 'papaparse';
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import AddPricePlanModal from "../../manufacturing/operations/addPricePlanModal";
import PricePlanningProxy from "../../../utility/proxy/manager/manufacturingProxy/pricePlanningProxy";
import UploadTenderImagesModal from "./uploadTenderImagesModal";
import AddTenderStoneRemarkModal from "./addTenderStoneRemarkModal";
import ViewImageModal from "./viewImageModal";
import AddTenderStoneModal from "./addTenderStoneModal";
import logo from '../../../images/pansuriya.jpg'
import { Modal } from "react-bootstrap";
import EditIcon from '@mui/icons-material/Edit';
import TenderLotBidProxy from "../../../utility/proxy/manager/tender/tenderLotBidProxy";
import ViewPricePlanModal from "./viewPricePlanModal";
import PageTitle from "../../common/pageTitle";

const TenderLotPlanning = () => {
    const { apiRef, handleCellClick } = Utility.useSingleClickEdit();
    const local_Permission = Constants.USER_PERMISSION.filter((iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.TENDER_OPERATION_TENDER_LOT_PLANNING);

    const [companyTender, setCompanyTender] = useState();
    const [companyTenderOption, setCompanyTenderOptions] = useState();
    const [companyTenderLoader, setCompanyTenderLoader] = useState(false);
    const [filterValues, setFilterValues] = useState({});
    const [tenderLot, setTenderLot] = useState([]);
    const [tenderLotLoader, setTenderLotLoader] = useState(false);
    const [tenderLotRow, setTenderLotRow] = useState("");
    const [columnNames, setColumnNames] = useState([]);
    const [selectionModel, setSelectionModel] = useState([]);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");
    const [showAddPricePlanModal, setShowAddPricePlanModal] = useState(false);
    const [tenderStoneId, setTenderStoneId] = useState("");
    const [tenderLotId, setTenderLotId] = useState("");
    const [showAddRemarkModal, setShowAddRemarkModal] = useState(false);
    const [remark, setRemark] = useState("");
    const [showUploadImgsModal, setShowUploadImgsModal] = useState(false);
    const [showViewImageModal, setShowViewImageModal] = useState(false);
    const [showAddTenderStoneModal, setShowAddTenderStoneModal] = useState(false);
    const [uploadLoader, setUploadLoader] = useState(false);
    const [tenderLotMap, setTenderLotMap] = useState(new Map());
    const [loader, setLoader] = useState(false);
    const setSnackbar = (showSnackbar, snackbarMessage, snackbarVariant) => {
        setShowSnackbar(showSnackbar);
        setSnackbarMessage(snackbarMessage);
        setSnackbarVariant(snackbarVariant);
    }
    const [selectedRowData, setSelectedRowData] = useState({});
    const [selectedStoneSize, setSelectedStoneSize] = useState("");
    const [selectedStonePieces, setSelectedStonePieces] = useState("");
    const [lotData, setLotData] = React.useState({});
    const [showStoneDataModal, setShowStoneDataModal] = React.useState(false); // to show data after view button clicked
    const [innerSelectionModel, setInnerSelectionModel] = useState(new Map());
    const [showPricePlanModal, setShowPricePlanModal] = useState(false);

    useEffect(() => {
        PageTitle("TENDER_OPERATION_TENDER_LOT_PLANNING");
        if (Utility.isReadAllowed(local_Permission))
            getCompanyTender();
    }, []);

    const getCompanyTender = () => {
        setCompanyTenderLoader(true);
        TenderLotProxy.GetAllCompanyTendersToEvaluate((response) => {
            if (response) {
                let optionArray = response.map((data) => {
                    return { value: data.tenderid, label: data.companyname + " | " + (data.tendername === null ? " - " : data.tendername), companyid: data.companyid };
                });
                setCompanyTenderOptions(optionArray);
                setCompanyTenderLoader(false);
            } else {
                setSnackbar(true, Constants.APiFailErrorMessage, "error");
                setCompanyTenderLoader(false);
            }
        })
    }

    const handleSearch = () => {
        setTenderLotLoader(true);
        // need to fetch such companies and tenders which are not yet locked
        // so both stone wise bid and lot wise bid status should be "IN_PROGRESS"
        TenderLotProxy.TenderLotDataByCompany(companyTender.value, companyTender.companyid, null, "IN_PROGRESS", "IN_PROGRESS", (response) => {
            if (response) {
                const uniqueFilterValues = {
                    minename: Utility.getDistinctOptions(response, "minename"),
                    tendername: Utility.getDistinctOptions(response, "tendername"),
                };
                prepareColumns(uniqueFilterValues);
                setFilterValues(uniqueFilterValues);
                setTenderLot(response);
                let tenderLotMap = {};
                response.forEach(iRes => tenderLotMap[iRes.tenderlotid] = iRes);
                setTenderLotMap(tenderLotMap);
                setTenderLotLoader(false);
            } else {
                setSnackbar(true, Constants.APiFailErrorMessage, "error");
                setTenderLotLoader(false);
            }
        });
    };

    const lockStoneWiseBid = () => {

        let data = [];

        setLoader(true);
        tenderLot.forEach(iData => {
            if (innerSelectionModel.has(iData.tenderlotid)) {


                innerSelectionModel.get(iData.tenderlotid).forEach(stoneid => {

                    let stone = iData.tenderstonelist.find(iRow => iRow.id === stoneid);;
                    data.push({
                        tenderstoneid: stoneid,
                        tenderlotid: iData.tenderlotid,
                        priceplanid: [],
                        lockedby: Constants.loggedInUser.id,
                        totalprice: stone.amount,
                        planid: null,  // no plan is locked here
                    });
                });
            }
        });

        TenderLotBidProxy.LockStoneWiseBid(data, (response) => {
            if (response && response.status === "Success") {
                setShowSnackbar(true);
                setSnackbarMessage("Locked Successfully");
                setSnackbarVariant("success");
                handleSearch();
                setLoader(false);
            } else {
                setShowSnackbar(true);
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
                setLoader(false);
            }
        })
    }

    const prepareColumns = (uniqueFilterValues) => {
        const columnNames = columnsWithoutPermission.map(woPermissionColumn => {
            let guids = [];

            // Determine which GUIDs (permissions) should be added based on the column field
            switch (woPermissionColumn.field) {
                case "tenderlotid":
                    guids = [Constants.COLUMN_PERMISSION.MFG_LOT_LOTCODE];
                    break;
                case "mineid":
                    guids = [Constants.COLUMN_PERMISSION.MFG_MINE_NAME];
                    break;
                case "pcs":
                    guids = [
                        Constants.COLUMN_PERMISSION.MFG_LOT_BASIC_PRICE,
                        Constants.COLUMN_PERMISSION.MFG_LOT_BASIC_AMOUNT
                    ];
                    break;
                default:
                    guids = [];
            }

            // Retrieve valid permission columns
            const permissionColumns = guids
                .map(id => Constants.USER_COLUMN_PERMISSION.find(permission => permission === id)) // Match permissions
                .filter(Boolean) // Remove undefined values (invalid permissions)
                .map(guid => {
                    const columnData = columnsWithPermission[guid]; // Get column details

                    // If the columnData is missing or an empty object, skip adding it
                    if (!columnData || Object.keys(columnData).length === 0) {
                        return null;
                    }

                    // Retrieve unique filter values for the column, defaulting to an empty array
                    const filterData = uniqueFilterValues[columnData.field] || [];

                    return {
                        ...columnData,  // Copy all existing column properties
                        valueOptions: filterData,  // Assign unique filter values
                    };
                })
                .filter(Boolean); // Remove any null values from the list

            // Modify the original column before adding it to the list
            const modifiedColumn = {
                ...woPermissionColumn,  // Retain original column properties
                valueOptions: uniqueFilterValues[woPermissionColumn.field] || [], // Assign unique filter values
            };

            // Return an array with the modified column and any valid permission columns
            return [modifiedColumn, ...permissionColumns];
        }).flat(); // Flatten the array

        //need to add Add-Stone Button if the user has screen permission
        if (Utility.isAddAllowed(local_Permission)) {
            columnNames.unshift(columnsWithPermission["check_screen_permission_add_stone"])
        }

        // Update state with the prepared columns
        setColumnNames(columnNames);
    };

    const prepareInnerColumns = (uniqueFilterValues) => {
        const columnNames = innercolumnsWithoutPermission.map(woPermissionColumn => {
            let guids = [];

            // Determine which GUIDs (permissions) should be added based on the column field
            switch (woPermissionColumn.field) {
                case "size":
                    guids = [Constants.COLUMN_PERMISSION.MFG_LOT_STONE_SIZE_PERCENT,
                    Constants.COLUMN_PERMISSION.MFG_STONE_PIECES
                    ];
                    break;
                default:
                    guids = [];
            }

            // Retrieve valid permission columns
            return fetchColumnDataFromGuid(guids, woPermissionColumn, innercolumnsWithPermission, uniqueFilterValues);

        }).flat(); // Flatten the array

        return columnNames;
    };

    const fetchColumnDataFromGuid = (guids, currentColumn, columnsWithPermission, uniqueFilterValues) => {
        const permissionColumns = guids
            .map(id => Constants.USER_COLUMN_PERMISSION.find(permission => permission === id)) // Match permissions
            .filter(Boolean) // Remove undefined values (invalid permissions)
            .map(guid => {
                const columnData = columnsWithPermission[guid]; // Get column details

                // If the columnData is missing or an empty object, skip adding it
                if (!columnData || Object.keys(columnData).length === 0) {
                    return null;
                }

                // Retrieve unique filter values for the column, defaulting to an empty array
                const filterData = uniqueFilterValues[columnData.field] || [];

                return {
                    ...columnData,  // Copy all existing column properties
                    valueOptions: filterData,  // Assign filter values
                };
            })
            .filter(Boolean); // Remove any null values from the list

        // Modify the original column before adding it to the list
        const modifiedColumn = {
            ...currentColumn,  // Retain original column properties
            valueOptions: uniqueFilterValues[currentColumn.field] || [], // Assign filter values
        };

        // Return an array with the modified column and any valid permission columns
        return [modifiedColumn, ...permissionColumns];
    };

    const columnsWithPermission = {
        "eabe76e1-e41b-40ef-b9ae-cbd13293bd1e": {
            field: "lotcode",
            headerName: "LOT CODE",
            headerAlign: "center",
            align: "center",
            width: 120,
            renderCell: renderCellExpand,
            filterOperators: Utility.defaultFilters(),
            sortComparator: (v1, v2) => Utility.sortAlphaNumeric(v1, v2)
        },
        "4a1de405-1802-4664-85e2-6ff7cf8c3bba": {
            field: "minename",
            headerName: "MINE NAME",
            headerAlign: "center",
            align: "center",
            width: 120,
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "3634473f-45cf-4def-8e69-c1f34ebe0f18": {
            field: "basicprice",
            headerName: "BASIC PRICE",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "number",
            valueFormatter: (params) => params.value ? (params.value).toFixed(2) : 0,
            renderCell: (params) => params.value ? (params.value).toFixed(2) : 0,
        },
        "394908cf-dc48-40ee-b68f-23efa651806e": {
            field: "basicamount",
            headerName: "BASIC AMOUNT",
            headerAlign: "center",
            align: "center",
            width: 115,
            type: "number",
            valueFormatter: (params) => params.value ? (params.value).toFixed(2) : 0,
            renderCell: (params) => params.value ? (params.value).toFixed(2) : 0,
        },
        "check_screen_permission_add_stone": {
            headerName: "ADD STONE",
            headerAlign: "center",
            field: "addstone",
            width: 115,
            align: "center",
            renderCell: (params) => {
                return (
                    <Tooltip title="Add stones to the lot">
                        <Button
                            sx={{ borderRadius: "3px", height: "28px", fontSize: "12px" }}
                            variant="contained"
                            component="label"
                            size="small"
                            onClick={() => {
                                setShowAddTenderStoneModal(true);
                                setTenderLotRow(params.row);
                            }}
                            disabled={!params.row.evaluate || (params.row.tenderstonelist && params.row.tenderstonelist.length > 0)}
                        >
                            ADD STONE
                        </Button>
                    </Tooltip >
                )
            },
        },
    };

    const columnsWithoutPermission = [
        {
            field: "",
            headerName: "",
            headerAlign: "center",
            align: "center",
            width: 70,
            renderCell: (params) => (
                <Button
                    sx={{
                        width: "44px !important",
                        height: "28px !important",
                        fontSize: "10px !important",
                        borderRadius: "12px !important",
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowStoneDataModal(true);
                        setLotData(params.row)
                    }}
                    component="label"
                    variant="contained"
                    size="small"
                >
                    VIEW
                </Button >
            ),
        },
        {
            field: "tenderlotid",
            headerName: "TENDER LOT ID",
            headerAlign: "center",
            align: "center",
            width: 120,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "mineid",
            headerName: "MINE ID",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "tenderid",
            headerName: "TENDER ID",
            headerAlign: "center",
            align: "center",
            width: 85,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "tendername",
            headerName: "TENDER",
            headerAlign: "center",
            align: "center",
            width: 250,
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "size",
            headerName: "SIZE",
            headerAlign: "center",
            align: "center",
            width: 76,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "description",
            headerName: "DESCRIPTION",
            headerAlign: "center",
            align: "center",
            width: 175,
            renderCell: renderCellExpand,
        },
        {
            field: "remark",
            headerName: "REMARK",
            headerAlign: "center",
            align: "center",
            width: 170,
            renderCell: renderCellExpand,
        },
        {
            field: "pcs",
            headerName: "PCS",
            headerAlign: "center",
            align: "center",
            width: 50,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "carats",
            headerName: "CARATS",
            headerAlign: "center",
            align: "center",
            width: 76,
            type: "number",
            valueFormatter: (params) => (params.value).toFixed(2),
            renderCell: (params) => (params.value).toFixed(2),
        },
        {
            field: "evaluate",
            headerName: "EVALUATE",
            headerAlign: "center",
            align: "center",
            width: 85,
            type: "boolean",
            renderCell: (params) => params.row.evaluate ? <CheckIcon color="success" /> : <ClearIcon color="error" />,
        },
    ];

    const innercolumnsWithPermission = {
        "86504376-84c2-4f7c-9187-bdd52e129f79": {
            field: "stonesizepercent",
            headerName: "STONE SIZE %",
            headerAlign: "center",
            align: "center",
            width: 120,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "9c0b6a70-9b92-4283-b27c-4e424c5d35ef": {
            field: "pieces",
            headerName: "PIECES",
            headerAlign: "center",
            align: "center",
            width: 60,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
    };

    const innercolumnsWithoutPermission = [
        {
            field: "id",
            headerName: "TENDER STONE ID",
            headerAlign: "center",
            align: "center",
            width: 138,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "size",
            headerName: "SIZE",
            headerAlign: "center",
            align: "center",
            width: 76,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "planids",
            headerName: "PLAN IDS",
            headerAlign: "center",
            align: "center",
            width: 165,
            renderCell: renderCellExpand,
        },
        {
            field: "",
            headerName: "",
            headerAlign: "center",
            align: "center",
            width: 70,
            renderCell: (params) => (
                <Button
                    sx={{
                        width: "44px !important",
                        height: "28px !important",
                        fontSize: "10px !important",
                        borderRadius: "12px !important",
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowPricePlanModal(true);
                        setTenderStoneId(params.row.id);
                        setTenderLotId(params.row.tenderlotid)
                    }}
                    component="label"
                    variant="contained"
                    size="small"
                >
                    VIEW
                </Button >
            ),
        },
        {
            field: "plan",
            headerName: "PLAN",
            headerAlign: "center",
            align: "center",
            width: 145,
            renderCell: (params) => {
                return (
                    <Button
                        sx={{ borderRadius: "3px", height: "28px", fontSize: "12px" }}
                        component="label"
                        variant="contained"
                        size="small"
                        onClick={() => {
                            setTenderStoneId(params.row.id);
                            setSelectedStoneSize(params.row.size)
                            setSelectedStonePieces(params.row.pieces)
                            const match = tenderLot.find(iData =>
                                iData.tenderstoneid && iData.tenderstoneid.includes(params.row.id)
                            );

                            if (match) {
                                setSelectedRowData(match);
                                setShowAddPricePlanModal(true);
                            }
                        }}
                    >
                        ADD PRICE PLAN
                    </Button>
                )
            },
        },
        {
            field: 'ppc',
            headerName: 'PPC',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            editable: true,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                        {params.value !== undefined && params.value !== null
                            ? parseFloat(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS)
                            : 0.00}
                    </span>
                    {params.colDef.editable && <EditIcon style={{ marginLeft: 3, marginBottom: 3, fontSize: '1rem' }} />}
                </div>
            ),
            cellClassName: (params) => 'lavenderCell',
            width: 120,
        },
        {
            field: 'amount',
            headerName: 'TOTAL',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            editable: true,
            valueFormatter: (params) => params.value,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                        {params.value !== undefined && params.value !== null
                            ? parseFloat(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS)
                            : 0.00}
                    </span>
                    {params.colDef.editable && <EditIcon style={{ marginLeft: 3, marginBottom: 3, fontSize: '1rem' }} />}
                </div>
            ),
            cellClassName: (params) => 'lavenderCell',
            width: 120,
        },
        {
            field: "remark",
            headerName: "REMARK",
            headerAlign: "center",
            align: "center",
            width: 150,
            renderCell: renderCellExpand,
        },
        {
            field: "addremark",
            headerName: "ADD REMARK",
            headerAlign: "center",
            align: "center",
            width: 115,
            renderCell: (params) => {
                return (
                    <Button
                        sx={{ borderRadius: "3px", height: "28px", fontSize: "12px" }}
                        component="label"
                        variant="contained"
                        size="small"
                        onClick={() => {
                            setShowAddRemarkModal(true);
                            setTenderStoneId(params.row.id);
                            setRemark(params.row.remark)
                        }}
                    >
                        REMARK
                    </Button>
                )
            },
        },
        {
            field: "upload",
            headerName: "UPLOAD",
            headerAlign: "center",
            align: "center",
            width: 75,
            renderCell: (params) => {
                return (
                    <Box
                        style={{ textAlign: "center", cursor: "pointer" }}
                        onClick={() => {
                            setTenderStoneId(params.row.id);
                            setShowUploadImgsModal(true);
                        }}
                    >
                        <MdOutlineAddPhotoAlternate color="primary" size={25} />
                    </Box>
                )
            },
        },
        {
            field: "noofimages",
            headerName: "VIEW IMG",
            headerAlign: "center",
            align: "center",
            width: 85,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => {
                return params.row.noofimages && params.row.noofimages > 0 ? (
                    <Badge
                        badgeContent={params.row.noofimages}
                        color="primary"
                        overlap="circular"
                        sx={{
                            "& .MuiBadge-badge": {
                                fontSize: "0.7rem",
                                width: "15px",
                                height: "17px",
                                borderRadius: "50%",
                            }
                        }}
                    >
                        <VideoLibraryTwoToneIcon
                            sx={{ cursor: "pointer", fontSize: "25px" }}
                            onClick={() => {
                                setShowViewImageModal(true);
                                setTenderStoneId(params.row.id);
                            }}
                        />
                    </Badge>
                ) : (
                    <span style={{ color: "black", fontStyle: "italic" }}>No Media</span>
                );
            },
        }
    ];

    const evaluateTenderLots = () => {

        if (selectionModel.length > 0) {

            let tenderLotIdEvaluated = [];
            let data = [];
            selectionModel.forEach(iTenderLotId => {
                if (tenderLotMap[iTenderLotId] && !tenderLotMap[iTenderLotId].evaluate) {
                    data.push({
                        tenderlotid: iTenderLotId,
                        size: parseFloat(tenderLotMap[iTenderLotId].carats),
                        stonesizepercent: 100,
                        pcs: tenderLotMap[iTenderLotId].pcs
                    });
                } else {
                    tenderLotIdEvaluated.push(iTenderLotId);
                }
            })

            if (tenderLotIdEvaluated.length > 0) {
                setSnackbar(true, "Below Tender Stone Ids are already evaluated : " + tenderLotIdEvaluated, "warning");
            } else {
                setTenderLotLoader(true);
                TenderLotProxy.EvaluateTenderLots(data, (response) => {
                    if (response.status === "Success") {
                        setSnackbar(true, response.errorMessage, "success");
                        setTenderLotLoader(false);
                        handleSearch();
                    } else {
                        setSnackbar(true, Constants.APiFailErrorMessage, "error");
                        setTenderLotLoader(false);
                    }
                });
            }

        }
    }

    const handleSave = (data) => {
        setLoader(true);
        PricePlanningProxy.AddPricePlanning(data, (response) => {
            if (response) {
                setSnackbar(true, "Pricing added successfully", "success");
                setLoader(false);
                setShowAddPricePlanModal(false);
                setTenderStoneId();
                handleSearch();
            } else {
                setSnackbar(true, Constants.APiFailErrorMessage, "error");
                setLoader(false);
                setShowAddPricePlanModal(false);
                setTenderStoneId();
            }
        });
    }

    const handleUpload = (file) => {
        setUploadLoader(true);
        const reader = new FileReader();
        reader.onload = () => {
            const text = reader.result;
            Papa.parse(text, {
                header: true,
                skipEmptyLines: true,
                complete: (result) => {
                    if (result.data.length <= 0) {
                        setSnackbar(true, "No Tender Stones Found", "warning");
                        setUploadLoader(false);
                        return;
                    }

                    let headersFromCSV = Object.keys(result.data[0]);
                    let requiredColumns = ['mineid', 'lotcode', 'size', 'pcs', 'carats', 'basic price', 'basic amount', 'description', 'remark'];
                    if (requiredColumns.length === headersFromCSV.length) {
                        let data = [];
                        let flag = true;
                        result.data.forEach(record => {
                            if (flag && record['mineid'] !== undefined && record['lotcode'] !== undefined &&
                                parseInt(record['mineid']) && companyTender && companyTender.value &&
                                record['size'] !== undefined && record['carats'] !== undefined && parseFloat(record['carats'])) {
                                let iRow = {
                                    mineid: parseInt(record['mineid']),
                                    lotcode: record['lotcode'],
                                    tenderid: companyTender.value,
                                    size: record['size'],
                                    carats: parseFloat(record['carats']),
                                    description: record['description'],
                                    pcs: record['pcs'],
                                    remark: record['remark'],
                                    basicprice: parseFloat(record['basic price']),
                                    basicamount: parseFloat(record['basic amount']),
                                    evaluate: record['evaluate']
                                }
                                data.push(iRow);
                            } else {
                                flag = false;
                            }
                            return true;
                        });

                        if (flag) {
                            TenderLotProxy.UploadTenderLot(data, (response) => {
                                if (response.status === "Success") {
                                    setSnackbar(true, response.errorMessage, "success");
                                    setUploadLoader(false);
                                    handleSearch();
                                } else {
                                    setSnackbar(true, response.status === "Error" ? response.errorMessage : Constants.APiFailErrorMessage, "error");
                                    setUploadLoader(false);
                                }
                            });
                        } else {
                            setSnackbar(true, "Please check the data and try again later.", "error");
                            setUploadLoader(false);
                        }
                    } else {
                        console.error('CSV columns do not match table columns.');
                        setSnackbar(true, "CSV columns do not match table columns.", "error");
                    }
                },
                error: (error) => {
                    console.error('Error parsing CSV:', error);
                    setSnackbar(true, 'Error parsing CSV:' + error, "error");
                }
            });
        };

        reader.readAsText(file);
    };

    const addRemark = (data) => {
        setLoader(true);
        TenderLotProxy.AddRemark(data, tenderStoneId, (response) => {
            if (response) {
                setSnackbar(true, "Remark Added Successfully", "success");
                setLoader(false);
                setTenderStoneId();
                setRemark("");
                handleSearch();
            } else {
                setSnackbar(true, Constants.APiFailErrorMessage, "error");
                setLoader(false);
                setTenderStoneId();
                setRemark("");
            }
        });
    }

    const addStones = (data) => {
        setTenderLotLoader(true);
        TenderLotProxy.AddStones(data, (response) => {
            if (response) {
                setSnackbar(true, "Stones Added Successfully", "success");
                setTenderStoneId();
                handleSearch();
                setShowAddTenderStoneModal(false);
                setTenderLotRow("");
            } else {
                setSnackbar(true, Constants.APiFailErrorMessage, "error");
                setTenderStoneId();
                setTenderLotLoader(false);
                setShowAddTenderStoneModal(false);
                setTenderLotRow("");
            }
        });
    }

    const handleImageUpload = (data, tenderstoneid) => {
        setLoader(true);
        TenderLotProxy.UploadTenderStoneImages(data, tenderstoneid, (response) => {
            if (response.status === "Success") {
                setSnackbar(true, response.errorMessage, "success");
                setLoader(false);
                setShowUploadImgsModal(false);
                setTenderStoneId();
                handleSearch();
            } else {
                setSnackbar(true, Constants.APiFailErrorMessage, "error");
                setLoader(false);
                setShowUploadImgsModal(false);
                setTenderStoneId();
                handleSearch();
            }
        })
    }

    const handleClear = () => {
        const fileInput = document.getElementById('file-input');
        fileInput.value = null;
    }

    const getChangedFields = (newRow, oldRow) => {
        const changedFields = [];
        Object.keys(newRow).forEach(field => {
            if (newRow[field] !== oldRow[field]) {
                changedFields.push({
                    id: newRow.id,
                    field: field,
                    value: newRow[field]
                });
            }
        });

        return changedFields;
    };

    const handleProcessRowUpdateError = (error) => {
        console.error(error);
    };

    const processRowUpdate = (newR, oldRow, rowsx,) => {
        const changedFeilds = getChangedFields(newR, oldRow);

        if (changedFeilds.length > 0) {
            const { id, field, value } = changedFeilds[0];
            let newRow = { ...oldRow };

            const updatedRow = rowsx.map(iRow => {
                const updatedTenderstonelist = iRow.tenderstonelist.map(row => {
                    if (row.id === id) {
                        if (field === "amount" && value && parseFloat(value) > 0 && parseFloat(value) !== parseFloat(row.amount)) {
                            const amount = parseFloat(value.toFixed(Constants.DEFAULT_NO_OF_DIGITS));

                            const newRow = {
                                ...row,
                                [field]: amount,
                                ppc: parseFloat((amount / row.size).toFixed(Constants.DEFAULT_NO_OF_DIGITS)),

                            };

                            return newRow;
                        } else if (field === "ppc" && value && parseFloat(value) > 0 && parseFloat(value) !== parseFloat(row.ppc)) {
                            const ppc = parseFloat(value.toFixed(Constants.DEFAULT_NO_OF_DIGITS));

                            const newRow = {
                                ...row,
                                [field]: ppc,
                                amount: parseFloat((ppc * row.size).toFixed(Constants.DEFAULT_NO_OF_DIGITS)),

                            };

                            return newRow;
                        }
                        else {
                            setShowSnackbar(true);
                            setSnackbarMessage("Incorrect Data");
                            setSnackbarVariant("warning");

                            return row;
                        }
                    }
                    return row;
                });

                return {
                    ...iRow,
                    tenderstonelist: updatedTenderstonelist,
                };
            });

            setTenderLot(updatedRow);
            return newRow;
        } else {
            // No changes detected, return the newRow to trigger exit edit mode
            return { ...newR };
        }
    }

    const getDetailPanelHeight = React.useCallback(() => 'auto', []);

    const getDetailPanelContent = React.useCallback(
        (row, innerSelectionModel1, mainData) => <DetailPanelContent row={row} mainData={mainData} innerSelectionModel1={innerSelectionModel1} />,
        [tenderLot],
    );

    function DetailPanelContent({ row: rowProp, innerSelectionModel1, mainData, isModalView }) {

        let row = [];
        const foundItem = mainData.find(function (idata) {
            return rowProp.tenderlotid === idata.tenderlotid;
        });
        if (foundItem && foundItem.tenderstonelist) {
            row = foundItem.tenderstonelist;
        } else {
            row = [];
        }

        let sortedrows = [...row];
        if (sortedrows.length > 0) {
            sortedrows = sortedrows.sort(
                (p1, p2) => (p1.id > p2.id) ? 1 : (p1.id < p2.id) ? -1 : 0);
        }

        let innercolumns = prepareInnerColumns(filterValues);

        return (

            <Stack
                sx={{ py: 1, height: '100%', boxSizing: 'border-box' }}
                direction="column"
            >
                <Box
                    sx={{
                        backgroundColor: '#fff',
                        color: '#000',
                        width: "78vw",
                        position: "relative",
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                            color: "#1976d2",
                            padding: "4px 5px",
                            borderRadius: "4px",
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .MuiTablePagination-toolbar, .MuiDataGrid-footerContainer": {
                            minHeight: "30px"
                        },
                        "& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                            margin: 0
                        },
                        "& .lavenderCell": {
                            color: "#080808",
                            backgroundColor: "#D8BFD8",
                        },
                        height: isModalView ? '60vh' : "auto",
                        marginLeft: '8px',
                    }}
                >
                    <DataGridPro
                        disableRowSelectionOnClick={true}
                        density="compact"
                        columns={innercolumns}
                        rows={sortedrows}
                        getRowId={(row) => row.id}
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                color: '#fff',
                                backgroundColor: 'grey',
                            },
                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                py: "8px",
                            },
                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                py: "15px",
                            },
                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                py: "22px",
                            },

                            fontSize: Constants.defaultTableFontSize, fontWeight: 'bold',
                            maxHeight: isModalView ? '90vh' : '30vh',
                        }}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                        initialState={{
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        checkboxSelection={true}
                        onRowSelectionModelChange={(newSelectionModel) => {
                            setInnerSelectionModel((prevSelection) => {
                                const updatedSelection = new Map(prevSelection);
                                if (newSelectionModel.length === 0) {
                                    updatedSelection.delete(rowProp.tenderlotid);
                                } else {
                                    updatedSelection.set(rowProp.tenderlotid, newSelectionModel);
                                }
                                return updatedSelection;
                            });
                        }}
                        rowSelectionModel={innerSelectionModel1.get(rowProp.tenderlotid) || []}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                        processRowUpdate={(newR, oldRow) => processRowUpdate(newR, oldRow, mainData)}
                        onProcessRowUpdateError={handleProcessRowUpdateError}
                        apiRef={apiRef}
                        onCellClick={handleCellClick}
                        editMode="cell"
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            top: "3px",
                            right: "265px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            "& .MuiButtonBase-root": {
                                minHeight: "30px",
                            },
                            "& .Mui-disabled": {
                                backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                                color: "rgba(0, 0, 0, 0.26) !important"
                            }
                        }}
                    >
                        <LoadingButton
                            sx={{ mr: 1 }}
                            variant="contained"
                            color="info"
                            onClick={(e) => lockStoneWiseBid()}
                            loading={tenderLotLoader}
                            loadingPosition="start"
                            startIcon={<BiSearchAlt2 />}
                            type="Submit"
                            disabled={!companyTender}
                        >
                            Lock
                        </LoadingButton>

                    </Box>
                </Box>
            </Stack>
        );
    }

    return !Utility.isReadAllowed(local_Permission) ? <NoPermission /> : (
        <Box>

            <CommonSnackbar
                show={showSnackbar}
                message={snackbarMessage}
                variant={snackbarVariant}
                autoHide={Constants.SnackBarCloseTimer}
                title={snackbarVariant === "success" ? "Success" :
                    snackbarVariant === "error" ? "Failed" : "Warning"}
                handleClose={() => setShowSnackbar(false)}
            />

            {/* just to view plans inserted in for the stone */}
            {showPricePlanModal && (
                <ViewPricePlanModal
                    tenderStoneId={tenderStoneId}
                    tenderLotId={tenderLotId}
                    show={true}
                    handleClose={() => setShowPricePlanModal(false)}
                />
            )}

            {/* shows stone data of the lot which can also be accessed by "+" button */}
            {showStoneDataModal ? (
                <Modal
                    backdrop="static"
                    show={true}
                    onHide={() => setShowStoneDataModal(false)}
                    dialogClassName="max-modalSize-med"
                    enforceFocus={false}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <img
                                src={logo}
                                height="40pt"
                                width="40pt"
                                alt={Constants.COMPANY_SITE}
                            />
                            &nbsp;&nbsp;Lot Details
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <DetailPanelContent
                            row={lotData}
                            mainData={tenderLot}
                            innerSelectionModel1={innerSelectionModel}
                            isModalView={true} />
                    </Modal.Body>
                </Modal>)
                : null}

            {showAddPricePlanModal ? (
                <AddPricePlanModal
                    show={showAddPricePlanModal}
                    loader={loader}
                    handleSave={handleSave}
                    handleClose={() => {
                        setShowAddPricePlanModal(false);
                        setTenderStoneId();
                        setSelectedRowData({})
                        setSelectedStonePieces("");
                        setSelectedStoneSize("");
                    }}
                    tenderstoneid={tenderStoneId}
                    fromTender={true}
                    lotCode={selectedRowData.lotcode}
                    totalLotPieces={selectedRowData.pcs}
                    totalLotCarats={selectedRowData.size}
                    stonePieces={selectedStonePieces}
                    stoneSize={selectedStoneSize}
                />
            ) : null}

            {showUploadImgsModal ? (
                <UploadTenderImagesModal
                    show={showUploadImgsModal}
                    loader={loader}
                    handleSave={handleImageUpload}
                    handleClose={() => {
                        setShowUploadImgsModal(false);
                        setTenderStoneId();
                    }}
                    tenderstoneid={tenderStoneId}
                />
            ) : null}

            {showAddRemarkModal ? (
                <AddTenderStoneRemarkModal
                    show={showAddRemarkModal}
                    remarkData={remark}
                    handleClose={() => setShowAddRemarkModal(false)}
                    handleSave={addRemark}
                />
            ) : null}

            {showViewImageModal ? (
                <ViewImageModal
                    show={showViewImageModal}
                    tenderstoneid={tenderStoneId}
                    handleClose={() => setShowViewImageModal(false)}
                />
            ) : null}

            {showAddTenderStoneModal ? (
                <AddTenderStoneModal
                    show={showAddTenderStoneModal}
                    handleClose={() => setShowAddTenderStoneModal(false)}
                    tenderlot={tenderLotRow}
                    loader={tenderLotLoader}
                    handleSave={addStones}
                />
            ) : null}

            <Box
                sx={{
                    margin: "10px 0",
                    display: "flex",
                    alignItems: "center"
                }}
            >

                {/* Company Tender */}
                <Typography>Company Tender</Typography>

                {/* Company Tender Loader */}
                <Select
                    menuPlacement="auto"
                    blurInputOnSelect={false}
                    value={companyTender}
                    isSearchable={true}
                    name="Company Tender"
                    menuPortalTarget={document.body}
                    options={companyTenderOption}
                    styles={{
                        ...Constants.Styles,
                        control: (base) => ({
                            ...base,
                            width: '30rem', // Adjust the width as needed
                            minHeight: "36px",
                            borderRadius: "3px",
                            marginLeft: "8px"
                        }),
                    }}
                    isClearable={true}
                    onChange={(selectedOption) => setCompanyTender(selectedOption)}
                />

                {/* Company Tender Loader */}
                {companyTenderLoader ? (
                    <Box sx={{ marginLeft: "8px" }}>
                        {<CircularProgress size={18} />}
                    </Box>
                ) : (
                    <Box sx={{ marginLeft: "8px" }}>
                        <Tooltip title="Reload List">
                            <RefreshOutlinedIcon
                                onClick={() => getCompanyTender()}
                                color="primary"
                                fontSize="medium"
                                sx={{ cursor: "pointer" }}
                            />
                        </Tooltip>
                    </Box>
                )}

                {/* Submit */}
                <LoadingButton
                    sx={{ ml: 3, borderRadius: "3px" }}
                    variant="contained"
                    color="info"
                    onClick={() => handleSearch()}
                    loading={tenderLotLoader}
                    loadingPosition="start"
                    startIcon={<BiSearchAlt2 />}
                    type="Submit"
                    disabled={!companyTender || companyTenderLoader}
                >
                    Search
                </LoadingButton>
            </Box>

            {/* Table Data */}
            <Box
                sx={{
                    backgroundColor: '#fff',
                    color: '#000',
                    width: "100%",
                    position: "relative",
                    "& .MuiTablePagination-selectLabel": {
                        marginTop: "15px",
                    },
                    "& .MuiTablePagination-displayedRows": {
                        marginTop: "15px",
                    },
                    "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                        fontWeight: Constants.defaultFontWeight,
                    },
                    "& .css-cc8tf1": {
                        fontWeight: Constants.defaultFontWeight,
                    },
                    "& .MuiTablePagination-toolbar, .MuiDataGrid-footerContainer": {
                        minHeight: "30px"
                    },
                    "& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                        margin: 0
                    },
                    height: "83vh",
                    "& .lavenderCell": {
                        color: "#080808",
                        backgroundColor: "#D8BFD8",
                    }
                }}
            >
                <DataGridPro
                    disableRowSelectionOnClick={true}
                    density="compact"
                    loading={tenderLotLoader}
                    sx={{
                        '& .MuiDataGrid-columnHeader': {
                            color: '#fff',
                            backgroundColor: 'grey',
                        },
                        "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                            py: "4px",
                        },
                        "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                            py: "12px",
                        },
                        "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                            py: "18px",
                        },

                        fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                    }}
                    slots={{ toolbar: GridToolbar }}
                    rows={tenderLot}
                    columns={columnNames}
                    getRowId={(row) => row.tenderlotid}
                    checkboxSelection={tenderLot.length > 0}
                    onRowSelectionModelChange={(newSelectionModel) => setSelectionModel(newSelectionModel)}
                    rowSelectionModel={selectionModel}
                    rowSpacingType="border"
                    initialState={{
                        columns: {
                            columnVisibilityModel: {
                                // Hide columns status and traderName, the other columns will remain visible
                                id: false,
                                mineid: false,
                                tenderid: false,
                            },
                        },
                        pagination: {
                            paginationModel: Constants.pageSize,
                        },
                    }}
                    pageSizeOptions={Constants.pageSizeOptions}
                    pagination
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 500 },
                        },
                    }}
                    getDetailPanelHeight={getDetailPanelHeight}
                    getDetailPanelContent={tenderLot.length === 0 ? null : ({ row }) => row.tenderstonelist.length > 0 ? getDetailPanelContent(row, innerSelectionModel, tenderLot) : null}
                />

                <Box sx={{
                    position: "absolute",
                    top: 5,
                    right: "260px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    "& .Mui-disabled": {
                        backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                        color: "rgba(0, 0, 0, 0.26) !important"
                    },
                }}>
                    {tenderLotLoader ? (
                        <CircularProgress size="1rem" />
                    ) : (
                        <Tooltip title="Reload List">
                            <RefreshOutlinedIcon
                                color="primary"
                                fontSize="medium"
                                onClick={() => handleSearch()}
                                sx={{ cursor: "pointer" }}
                            />
                        </Tooltip>
                    )}

                    <LoadingButton
                        sx={{ height: "32px" }}
                        variant="contained"
                        color="info"
                        onClick={(e) => evaluateTenderLots()}
                        loading={tenderLotLoader}
                        loadingPosition="start"
                        startIcon={<AssignmentTurnedInIcon />}
                        type="Submit"
                        disabled={selectionModel.length === 0}
                    >
                        Evaluate
                    </LoadingButton>

                    {Utility.isAddAllowed(local_Permission) ? (
                        <Box>
                            <LoadingButton
                                color="info"
                                variant="outlined"
                                size="small"
                                sx={{ m: "0 6px 0 3px" }}
                                loading={uploadLoader}
                                component="label"
                                loadingPosition="start"
                                startIcon={<UploadFileIcon size="large" />}
                                disabled={!companyTender}
                            >
                                Upload
                                <input
                                    id="file-input"
                                    hidden
                                    accept=".csv"
                                    type="file"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            setUploadLoader(true);
                                            handleUpload(file);
                                        }
                                    }}
                                    onClick={(e) => {
                                        handleClear()
                                    }}
                                />
                            </LoadingButton>

                            <a
                                href="/demo_excel/CompanyTenderLotUpload.csv"
                                download="CompanyTenderLotUpload.csv"
                                style={{ color: "red" }}
                            >
                                <BackupTable size={25} />
                            </a>
                        </Box>
                    ) : null}
                </Box>
            </Box>

        </Box>
    );
}

export default TenderLotPlanning;